import PropTypes from 'prop-types';
import React from 'react';
import { Button, Col, Modal, Row } from 'react-bootstrap';
import { Formik } from 'formik';
import * as Yup from 'yup';

import toast from 'Shared/toast';
import './PatientMessagingEnrollmentModalStyle.scss';
import { errorHandler } from '../../../../utils';
import PESService from '../../../../services/PESService';
import { patientSubscriptionPropType } from 'Shared/types';
import { localize } from 'App/utils';
import SubmitButton from 'Shared/form/SubmitButton';
import FormButtonGroup from 'App/components/styles/FormButtonGroup';
import { PhoneField } from 'App/components/PatientForm/components/PhoneField';
import { dateTimeSlashesFormat } from 'App/services/DateService';
import PreviousEnrollmentMessage from './PatientEducationMessages/PreviousEnrollmentMessage';
import NoPreviousEnrollmentMessage from './PatientEducationMessages/NoPreviousEnrollmentMessage';

const propTypes = {
    onHide: PropTypes.func.isRequired,
    show: PropTypes.bool,
    subscriptionValue: patientSubscriptionPropType,
    mobilePhone: PropTypes.string,
    patientId: PropTypes.string,
};

const defaultProps = {
    show: false,
    subscriptionValue: null,
    mobilePhone: '',
    patientId: '',
};

const validationSchema = Yup.object().shape({
    phone: Yup.string()
        .matches(/\d{10}/, 'Invalid phone number')
        .required()
        .label('Patient Mobile Phone'),
});

function PatientMessagingEnrollmentModal({ onHide, show, subscriptionValue, mobilePhone, patientId }) {
    const [isDeclining, setIsDeclining] = React.useState(false);
    const shouldShowDeclineSmsButton = !subscriptionValue;
    const isDeclinedSubscription = Boolean(
        subscriptionValue?.unsubscribedOn && !subscriptionValue?.subscribedOn && !subscriptionValue?.confirmed
    );

    const onSubmit = (values) => {
        if (subscriptionValue && !isDeclinedSubscription && mobilePhone === values.phone) {
            onHide(false);
        } else {
            PESService.updatePatient(patientId, { mobile_phone: values.phone })
                .then(() => {
                    return PESService.subscribeSMS(patientId).then(() => {
                        onHide(true);
                        toast.success('Patient enrolled successfully');
                    });
                })
                .catch((error) => {
                    errorHandler(error);
                    onHide(false);
                });
        }
    };

    const onPatientDeclinedEnrollment = () => {
        setIsDeclining(true);
        PESService.declinePatientSubscription(patientId)
            .then(() => {
                onHide(true);
                toast.success('Patient declined enrollment successfully');
            })
            .catch((error) => {
                errorHandler(error);
                onHide(false);
            })
            .finally(() => setIsDeclining(false));
    };

    return (
        <Modal
            show={show}
            onHide={onHide}
            backdrop="static"
            className="patientMessagingModal"
            data-testid="patient-messaging-enrollment-modal"
            size="lg"
        >
            <Modal.Header closeButton={false}>
                <Modal.Title role="heading" className="header">
                    Patient Messaging
                </Modal.Title>
            </Modal.Header>
            <Formik
                initialValues={{
                    phone: mobilePhone || '',
                }}
                onSubmit={(values, actions) => {
                    actions.setSubmitting(false);
                    onSubmit(values);
                }}
                validationSchema={validationSchema}
            >
                {({ handleSubmit }) => (
                    <form onSubmit={handleSubmit}>
                        <Modal.Body>
                            <Row>
                                <Col sm={7}>
                                    <div className="enrollment-information">
                                        {`Enrolled: ${subscriptionValue?.status === true ? 'Yes' : 'No'}`}
                                    </div>
                                </Col>
                                {subscriptionValue?.status && (
                                    <Col sm={5}>
                                        <div className="enrollment-information">
                                            <div>
                                                Enrollment Date:&nbsp;
                                                <span>
                                                    {localize(subscriptionValue?.subscribedOn, dateTimeSlashesFormat)}
                                                </span>
                                            </div>
                                        </div>
                                    </Col>
                                )}
                            </Row>
                            <Row>
                                <Col sm={12}>
                                    <Row>
                                        <Col sm={12}>
                                            <PhoneField
                                                fieldName="phone"
                                                required
                                                placeholder="(###) ###-####"
                                                label="Patient Mobile Phone"
                                            />
                                        </Col>
                                    </Row>
                                    {subscriptionValue && !isDeclinedSubscription ? (
                                        <PreviousEnrollmentMessage />
                                    ) : (
                                        <NoPreviousEnrollmentMessage />
                                    )}
                                </Col>
                            </Row>
                        </Modal.Body>

                        <Modal.Footer>
                            <FormButtonGroup>
                                {shouldShowDeclineSmsButton && (
                                    <Button
                                        className="patient-declined-sms-button"
                                        variant="danger"
                                        type="button"
                                        onClick={onPatientDeclinedEnrollment}
                                        disabled={isDeclining}
                                    >
                                        Patient Declined SMS
                                    </Button>
                                )}
                                <Button
                                    variant="outline-secondary"
                                    type="button"
                                    onClick={() => {
                                        onHide(false);
                                    }}
                                >
                                    Cancel
                                </Button>
                                <SubmitButton
                                    ignoreDirty={!subscriptionValue || isDeclinedSubscription}
                                    disabled={isDeclining}
                                >
                                    Submit
                                </SubmitButton>
                            </FormButtonGroup>
                        </Modal.Footer>
                    </form>
                )}
            </Formik>
        </Modal>
    );
}

PatientMessagingEnrollmentModal.propTypes = propTypes;
PatientMessagingEnrollmentModal.defaultProps = defaultProps;

export default PatientMessagingEnrollmentModal;
